import {
  Mesh,
  OrthographicCamera,
  Scene,
  PlaneBufferGeometry,
  Texture,
  Material,
  MeshBasicMaterial,
  DoubleSide,
  LinearFilter,
  Geometry,
  BufferGeometry,
  Vector2,
  WebGLRenderer,
} from 'three'

type CanvasStyle = string | CanvasGradient | CanvasPattern

interface UVTextureColorOptions {
  fillStyle: CanvasStyle
  strokeStyle: CanvasStyle
  strokeWidth: number
}

type PartialUVTextureColorOptions = Partial<UVTextureColorOptions>

const UV_TEXTURE_DEFAULT_COLOR_OPTIONS: UVTextureColorOptions = {
  fillStyle: 'black',
  strokeStyle: 'rgb(0, 255, 0)',
  strokeWidth: 1,
}

const fillDefaultColorOptions = (
  options: PartialUVTextureColorOptions
): UVTextureColorOptions => ({
  fillStyle: options.fillStyle || UV_TEXTURE_DEFAULT_COLOR_OPTIONS.fillStyle,
  strokeStyle:
    options.strokeStyle || UV_TEXTURE_DEFAULT_COLOR_OPTIONS.strokeStyle,
  strokeWidth:
    options.strokeWidth || UV_TEXTURE_DEFAULT_COLOR_OPTIONS.strokeWidth,
})

const getWidthHeight = (canvas: HTMLCanvasElement | null = null) =>
  [
    canvas === null ? window.innerWidth : canvas.width,
    canvas === null ? window.innerHeight : canvas.height,
  ] as const

export class UVMapViewer {
  private userAutoClearSetting: boolean = false

  private frame = {
    x: 10,
    y: 10,
    width: 256,
    height: 256,
  }

  private textureSettings = {
    size: 1024,
    scale: 0.95,
  }

  private position = {
    x: this.frame.x,
    y: this.frame.y,
  }

  private size = {
    width: this.frame.width,
    height: this.frame.height,
  }

  private targetMesh: Mesh
  private targetUVChannel = 0

  private camera: OrthographicCamera
  private scene: Scene

  private plane: PlaneBufferGeometry

  private uvMapTexture: Texture | null = null
  private material: Material

  private mesh: Mesh
  private texture: Texture | null = null
  private parentCanvas: HTMLCanvasElement | null = null

  public enabled = true

  private canvas: HTMLCanvasElement | null = null
  private context: CanvasRenderingContext2D | null = null

  private colorOptions: UVTextureColorOptions

  public constructor(
    mesh: Mesh,
    uvChannel = 0,
    colorOptions: PartialUVTextureColorOptions = UV_TEXTURE_DEFAULT_COLOR_OPTIONS,
    parentCanvas: HTMLCanvasElement | null = null,
    texture: Texture | null = null
  ) {
    this.targetMesh = mesh
    this.targetUVChannel = uvChannel
    this.parentCanvas = parentCanvas
    this.texture = texture

    this.colorOptions = fillDefaultColorOptions(colorOptions)

    const [canvasWidth, canvasHeight] = getWidthHeight(this.parentCanvas)
    this.camera = new OrthographicCamera(
      canvasWidth / -2,
      canvasWidth / 2,
      canvasHeight / 2,
      canvasHeight / -2,
      1,
      10
    )
    this.camera.position.set(0, 0, 2)

    this.scene = new Scene()

    this.plane = new PlaneBufferGeometry(this.frame.width, this.frame.height)

    this.updateUVChannelTexture()

    this.material = new MeshBasicMaterial({
      map: this.uvMapTexture,
      side: DoubleSide,
    })

    this.mesh = new Mesh(this.plane, this.material)

    this.scene.add(this.mesh)

    this.update()
  }

  public render(renderer: WebGLRenderer) {
    if (this.enabled) {
      this.updateUVChannelTexture()

      this.userAutoClearSetting = renderer.autoClear
      renderer.autoClear = false // Allow rendering of overlay.
      renderer.clearDepth()
      renderer.render(this.scene, this.camera)
      renderer.autoClear = this.userAutoClearSetting
    }
  }

  public resize() {
    if (this.enabled) {
      const [canvasWidth, canvasHeight] = getWidthHeight(this.parentCanvas)

      this.camera.left = canvasWidth / -2
      this.camera.right = canvasWidth / 2
      this.camera.top = canvasHeight / 2
      this.camera.bottom = canvasHeight / -2
      this.camera.updateProjectionMatrix()

      this.update()
    }
  }

  public setPosition(x: number, y: number) {
    this.position.x = x
    this.position.y = y

    const [canvasWidth, canvasHeight] = getWidthHeight(this.parentCanvas)

    this.mesh.position.set(
      -canvasWidth / 2 + this.size.width / 2 + this.position.x,
      canvasHeight / 2 - this.size.height / 2 - this.position.y,
      0
    )
  }

  private resetPosition() {
    this.setPosition(this.position.x, this.position.y)
  }

  public setSize(width: number, height: number) {
    this.size.width = width
    this.size.height = height

    this.mesh.scale.set(
      this.size.width / this.frame.width,
      this.size.height / this.frame.height,
      1
    )

    this.resetPosition()
  }

  public setTexture(texture: Texture) {
    this.texture = texture
  }

  private update() {
    this.setPosition(this.position.x, this.position.y)
    this.setSize(this.size.width, this.size.height)
  }

  private updateUVChannelTexture() {
    const firstUpdate = !this.canvas

    if (firstUpdate) {
      this.canvas = document.createElement('canvas')
      this.context = this.canvas.getContext('2d')

      this.canvas.width = this.textureSettings.size
      this.canvas.height = this.textureSettings.size
    }

    const realSize = this.textureSettings.size * this.textureSettings.scale
    const offset = (this.textureSettings.size - realSize) / 2

    const uvs =
      this.targetMesh.geometry instanceof Geometry
        ? this.targetMesh.geometry.faceVertexUvs[this.targetUVChannel]
        : null

    if (uvs === null) {
      const bufferGeometry = this.targetMesh.geometry as BufferGeometry

      console.log({
        "bufferGometry.getAttribute('uv')": bufferGeometry.getAttribute('uv'),
      })

      throw new Error(
        'BufferGeometry is not yet supported for UV layout rendering.'
      )
    }

    if (this.context === null) {
      throw new Error('Failed to initialize UV map viewer context.')
    }

    if (this.canvas === null) {
      throw new Error('Failed to initialize UV map viewer canvas.')
    }

    this.context.fillStyle = this.colorOptions.fillStyle
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height)

    if (this.texture && this.texture.image instanceof HTMLImageElement) {
      this.context.drawImage(
        this.texture.image,
        offset,
        offset,
        realSize,
        realSize
      )
    }

    this.drawCoordinateCorners()
    this.drawUVLines(uvs, offset, realSize)

    if (!this.uvMapTexture) {
      this.uvMapTexture = new Texture(this.canvas)
      this.uvMapTexture.magFilter = LinearFilter
      this.uvMapTexture.minFilter = LinearFilter
    }

    this.uvMapTexture.needsUpdate = true
  }

  private drawCoordinateText(
    text: string,
    color: string,
    size: number,
    x: number,
    y: number,
    options: {
      align?: {
        x?: CanvasTextAlign
        y?: CanvasTextBaseline
      }
    } | null = null
  ) {
    if (this.context === null) {
      throw new Error('Failed to initialize UV map viewer context.')
    }

    const fontSize = Math.floor(this.textureSettings.size * size)
    this.context.font = `${fontSize}px monospace`
    this.context.textAlign = options?.align?.x ?? 'center'
    this.context.textBaseline = options?.align?.y ?? 'middle'
    this.context.fillStyle = color
    this.context.fillText(text, x, y)
  }

  private drawCoordinateCorners() {
    if (this.canvas === null) {
      throw new Error('Failed to initialize UV map viewer canvas.')
    }

    const fontSize = 0.02
    const cornerLabelOffset = Math.floor(this.textureSettings.size * 0.02)
    const color = 'white'
    this.drawCoordinateText(
      '0,0',
      color,
      fontSize,
      0 + cornerLabelOffset,
      this.canvas.height - cornerLabelOffset,
      {
        align: {
          x: 'left',
          y: 'bottom',
        },
      }
    )
    this.drawCoordinateText(
      '0,1',
      color,
      fontSize,
      0 + cornerLabelOffset,
      0 + cornerLabelOffset,
      {
        align: {
          x: 'left',
          y: 'top',
        },
      }
    )
    this.drawCoordinateText(
      '1,0',
      color,
      fontSize,
      this.canvas.width - cornerLabelOffset,
      this.canvas.height - cornerLabelOffset,
      {
        align: {
          x: 'right',
          y: 'bottom',
        },
      }
    )
    this.drawCoordinateText(
      '1,1',
      color,
      fontSize,
      this.canvas.width - cornerLabelOffset,
      0 + cornerLabelOffset,
      {
        align: {
          x: 'right',
          y: 'top',
        },
      }
    )
  }

  private drawUVLines(uvs: Vector2[][], offset: number, realSize: number) {
    if (this.context === null) {
      throw new Error('Failed to initialize UV map viewer context.')
    }

    const normalizePosition = (uvPos: Vector2): Vector2 => {
      const normalizedX = offset + realSize * uvPos.x
      const normalizedY = offset + realSize * (1 - uvPos.y) // Canvas starts from top-left, invert here.

      return new Vector2(normalizedX, normalizedY)
    }

    this.context.beginPath()

    uvs.forEach((faceUVs) => {
      if (this.context === null) {
        throw new Error('Failed to initialize UV map viewer context.')
      }

      const np = faceUVs.map(normalizePosition)
      this.context.moveTo(np[0].x, np[0].y)
      this.context.lineTo(np[1].x, np[1].y)
      this.context.lineTo(np[2].x, np[2].y)
      this.context.lineTo(np[0].x, np[0].y)
    })

    this.context.closePath()

    this.context.strokeStyle = this.colorOptions.strokeStyle
    this.context.lineWidth = this.colorOptions.strokeWidth
    this.context.stroke()
  }
}
